/* PTL → PTL Billing → Billing Review.
   One cycle's billing lines (D7): filters shared by both tabs, status cards and margin cards that filter, the
   Shipment view with bulk approve (caps from the settings, all or nothing on the server) and the Client view.
   Approved lines can be re-opened with a note, fixed and sent for approval again. Amounts exclude GST. */
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { kpiRowSx } from '../../common/sharedStyles'
import { BILLING_BASE, useApproveLinesMutation, useBillingConfigQuery, useClientRowsQuery, useReopenLineMutation, useResubmitLineMutation,
  useSearchLinesQuery, useSelectableLinesMutation } from '../../redux/ptlBillingApiSlice'
import KpiTile from './components/KpiTile'
import MerchantPicker from './components/MerchantPicker'
import FilterSelect, { FilterSlot } from './components/FilterSelect'
import { ApplyButton, FilterActions, FilterCard, FilterCardContent, FilterRow, ResetButton } from '../../common/FilterBar'
import { apiConstants, getDownload } from '../../common/constants'
import LineDetailDialog, { STATUS_CHIP } from './components/LineDetailDialog'
import { AgreementChips } from './components/AgreementCard'
import { creditDaysText } from '../ptl-merchant-setup/components/MerchantStatus'
import { canBill, downloadFile, errText, fmtDate, inr, monthLabel, pct, recentMonths, safeMonth } from './billingUtils'

const SORTS = [
  ['default', 'Default (client, pickup date)'], ['margin_asc', 'Margin % (low to high)'], ['margin_desc', 'Margin % (high to low)'],
  ['marginamt_asc', 'Margin ₹ (low to high)'], ['total_desc', 'Bill value (high to low)'], ['total_asc', 'Bill value (low to high)'],
  ['cost_desc', 'Courier cost (high to low)'], ['picked_asc', 'Pickup date (oldest first)'], ['picked_desc', 'Pickup date (newest first)'],
  ['delivered_desc', 'Delivered date (newest first)'], ['wt_desc', 'Weight (high to low)'], ['client_asc', 'Client (A to Z)'],
]
const KEYS = ['merchant', 'courier', 'lane', 'slab', 'status', 'adhoc', 'margin', 'blocked', 'q', 'sort']

export default function BillingReview() {
  const [params, setParams] = useSearchParams()
  const month = safeMonth(params.get('month'))
  const tab = params.get('tab') === 'client' ? 'client' : 'ship'
  const p = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || '']))
  const page = Number(params.get('page') || 1)
  const size = Number(params.get('size') || 50)
  const set = (patch) => {
    const next = { month, tab, ...p, page: 1, size, ...patch }
    setParams(Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== '' && v !== null && v !== undefined && !(k === 'page' && v === 1) && !(k === 'tab' && v === 'ship'))))
  }
  const filter = {
    cycleMonth: month, merchantOids: p.merchant ? [p.merchant] : undefined, couriers: p.courier ? [p.courier] : undefined,
    lane: p.lane || undefined, weightSlab: p.slab || undefined, status: p.status || undefined, adhoc: p.adhoc || undefined,
    margin: p.margin || undefined, blocked: p.blocked === '' ? undefined : p.blocked === 'true', q: p.q || undefined, sort: p.sort || undefined,
  }
  const { data, isFetching, error } = useSearchLinesQuery({ ...filter, page, size })
  const { data: clients, isFetching: clientsLoading } = useClientRowsQuery(filter, { skip: tab !== 'client' })
  const { data: config } = useBillingConfigQuery()
  const billing = canBill()
  const { enqueueSnackbar } = useSnackbar()
  // every shipment matching the filters (any status, no paging), as CSV for Excel
  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    const q = new URLSearchParams(Object.entries({ month, merchant: p.merchant, courier: p.courier, lane: p.lane, slab: p.slab, status: p.status,
      adhoc: p.adhoc, margin: p.margin, blocked: p.blocked, q: p.q, sort: p.sort }).filter(([, v]) => v))
    setExporting(true)
    try {
      await downloadFile(getDownload, `${apiConstants.BASE_LOCAL}${BILLING_BASE}/lines/export.csv?${q}`, `PTL_Billing_Shipments_${month}.csv`)
    } catch (e) {
      enqueueSnackbar('Could not download the shipments.', { variant: 'error' })
    } finally {
      setExporting(false)
    }
  }
  const [approve, { isLoading: approving }] = useApproveLinesMutation()
  const [selectFirst, { isLoading: selecting }] = useSelectableLinesMutation()
  const [resubmit] = useResubmitLineMutation()
  const [selected, setSelected] = useState({}) // id → { version, merchantOid, totalExclGst }
  const [detailId, setDetailId] = useState(null)
  const [reopenRow, setReopenRow] = useState(null)
  const [qInput, setQInput] = useState(p.q)

  useEffect(() => { setSelected({}) }, [month]) // a selection never spans months
  const selIds = Object.keys(selected).map(Number)
  const selClients = new Set(Object.values(selected).map((s) => s.merchantOid)).size
  const selAmount = Object.values(selected).reduce((a, s) => a + Number(s.totalExclGst || 0), 0)
  // caps come from Billing settings only (the server enforces them too); unknown until the settings load
  const maxShip = config?.maxShipmentsPerApproval
  const maxClients = config?.maxClientsPerApproval
  const overShip = maxShip != null && selIds.length > maxShip
  const overClients = maxClients != null && selClients > maxClients

  const onSelection = (ids) => {
    const rowsById = Object.fromEntries((data?.rows || []).map((r) => [r.id, r]))
    const next = {}
    ids.forEach((id) => {
      const r = rowsById[id]
      next[id] = r ? { version: r.version, merchantOid: r.merchantOid, totalExclGst: r.totalExclGst } : selected[id]
    })
    setSelected(next)
  }
  const doSelectFirst = async () => {
    const res = await selectFirst({ filter, limit: maxShip })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    setSelected(Object.fromEntries(res.data.map((r) => [r.id, r])))
    enqueueSnackbar(`${res.data.length} pending shipments selected`, { variant: 'info' })
  }
  const doApprove = async () => {
    const lines = Object.entries(selected).map(([id, s]) => ({ id: Number(id), version: s.version }))
    const res = await approve(lines)
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar(`Approved ${res.data.approved} shipments (${res.data.clients} clients, ${inr(res.data.amount)})`, { variant: 'success' })
    setSelected({})
  }
  const doResubmit = async (row) => {
    const res = await resubmit({ id: row.id, version: row.version })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar(`${row.awb} sent for approval again (total ${inr(res.data.totalExclGst)})`, { variant: 'success' })
  }

  const cards = data?.statusCards || {}
  const curCard = p.adhoc === 'INCOMPLETE' ? 'ADHOC_INCOMPLETE' : p.blocked === 'true' ? 'BLOCKED' : (p.status || 'ALL')
  const statusCard = (key, label, accent, sub) => (
    <KpiTile key={key} label={label} accent={accent} value={cards[key]?.count ?? '—'} sub={sub ?? inr(cards[key]?.amount, 0)} selected={curCard === key}
      onClick={() => set(key === 'ADHOC_INCOMPLETE' ? { status: '', blocked: '', adhoc: 'INCOMPLETE' } : key === 'BLOCKED' ? { status: '', adhoc: '', blocked: 'true' }
        : { status: key === 'ALL' ? '' : key, adhoc: '', blocked: '' })} />
  )
  // margin % over the lines with a known courier cost only; lines with no price or no courier cost are left out
  const marginPct = data && data.marginBaseTotal > 0 && data.estCourierCost > 0 ? data.margin / data.marginBaseTotal : null
  const leftOut = data?.marginLeftOut ? ` · ${data.marginLeftOut} left out (no price / no courier cost)` : ''

  const columns = useMemo(() => [
    { field: 'awb', headerName: 'AWB', minWidth: 140, renderCell: ({ row }) => <Box>
      <Button size='small' onClick={() => setDetailId(row.id)} sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}>{row.awb}</Button>
      {row.outcome !== 'DELIVERED' && <Chip size='small' color='error' variant='outlined' label={row.outcome} sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
      {row.rolledOverFrom && <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>from {row.rolledOverFrom}</Typography>}</Box> },
    { field: 'merchantName', headerName: 'Client', flex: 1, minWidth: 140 },
    { field: 'courierName', headerName: 'Courier', minWidth: 120 },
    { field: 'lane', headerName: 'Zone', minWidth: 100 },
    { field: 'chargeableWeightKg', headerName: 'Weight (kg)', minWidth: 90, align: 'right', headerAlign: 'right' },
    { field: 'pickupAt', headerName: 'Picked up', minWidth: 100, renderCell: ({ row }) => fmtDate(row.pickupAt) },
    { field: 'outcomeAt', headerName: 'Delivered', minWidth: 100, renderCell: ({ row }) => fmtDate(row.outcomeAt) },
    { field: 'baseTotal', headerName: 'Base', minWidth: 100, align: 'right', headerAlign: 'right', renderCell: ({ row }) => inr(row.baseTotal) },
    { field: 'surcharges', headerName: 'DPH + surcharges', minWidth: 110, align: 'right', headerAlign: 'right', renderCell: ({ row }) => inr(Number(row.dhp) + Number(row.surchargeTotal)) },
    { field: 'adhocClientTotal', headerName: 'Ad hoc (client)', minWidth: 110, align: 'right', headerAlign: 'right', renderCell: ({ row }) => <Box sx={{ textAlign: 'right' }}>
      {inr(Number(row.adhocClientTotal) + Number(row.legacyAdhocTotal))}{row.adhocIncomplete > 0 && <Chip size='small' color='warning' label={`${row.adhocIncomplete} INCOMPLETE`} sx={{ height: 18, fontSize: 10, display: 'flex' }} />}</Box> },
    { field: 'totalExclGst', headerName: 'Total (excl. GST)', minWidth: 120, align: 'right', headerAlign: 'right', renderCell: ({ row }) => <b>{inr(row.totalExclGst)}</b> },
    { field: 'estCourierCost', headerName: 'Courier cost', minWidth: 100, align: 'right', headerAlign: 'right', renderCell: ({ row }) => inr(row.estCourierCost) },
    { field: 'marginPct', headerName: 'Margin', minWidth: 80, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => <Typography sx={{ fontSize: 13, color: row.margin == null ? 'text.secondary' : row.margin < 0 ? 'error.main' : 'success.dark' }}>{pct(row.marginPct)}</Typography> },
    { field: 'status', headerName: 'Status', minWidth: 150, renderCell: ({ row }) => <Box>
      <Chip size='small' color={STATUS_CHIP[row.status]?.color} label={STATUS_CHIP[row.status]?.label || row.status} />
      {row.blocked && <Typography sx={{ fontSize: 10, color: 'error.main' }}>Blocked: {(row.blockingMessages || row.flagMessages || [])[0]}</Typography>}
      {row.status === 'REOPENED' && row.reopenNote && <Typography sx={{ fontSize: 10, color: 'text.secondary', whiteSpace: 'normal' }}>{row.reopenNote}</Typography>}</Box> },
    { field: 'actions', headerName: 'Action', minWidth: 170, sortable: false, renderCell: ({ row }) => (
      row.status === 'APPROVED' && billing ? <Button size='small' variant='outlined' onClick={() => setReopenRow(row)} sx={{ textTransform: 'none', borderRadius: 5 }}>Re-open</Button>
        : row.status === 'REOPENED' ? <Button size='small' variant='outlined' onClick={() => doResubmit(row)} sx={{ textTransform: 'none', borderRadius: 5 }}>Fixed · send for approval</Button>
          : <Button size='small' onClick={() => setDetailId(row.id)} sx={{ textTransform: 'none' }}>Details</Button>) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [billing, data])

  return (
    <Box>
      <PageTitle>Billing Review</PageTitle>
      <FilterCard>
        <FilterCardContent>
          {/* row 1: what to look at; row 2: the shipment filters */}
          <FilterRow>
            <FilterSelect label='Service month' value={month} allLabel={null} onChange={(v) => setParams({ month: v })} options={recentMonths(12)} />
            <FilterSlot label='Client' sx={{ flex: '2 1 240px' }}>
              <MerchantPicker value={p.merchant || null} onChange={(m) => set({ merchant: m || '' })} sx={{ minWidth: 0 }} />
            </FilterSlot>
            <FilterSlot label='Shipment (AWB)' sx={{ flex: '2 1 220px' }}>
              <TextField size='small' fullWidth value={qInput} onChange={(e) => setQInput(e.target.value)} inputProps={{ 'aria-label': 'Shipment (AWB)' }}
                onKeyDown={(e) => { if (e.key === 'Enter') set({ q: qInput.trim() }) }} placeholder='Type an AWB and press Enter' />
            </FilterSlot>
            <FilterSelect label='Sort by' value={p.sort || 'default'} allLabel={null} onChange={(v) => set({ sort: v === 'default' ? '' : v })}
              options={SORTS.map(([v, l]) => ({ value: v, label: l }))} sx={{ flex: '2 1 220px' }} />
            <FilterActions>
              <ApplyButton onClick={exportCsv} disabled={exporting}>{exporting ? 'Preparing…' : 'Download CSV'}</ApplyButton>
              <ResetButton onClick={() => { setQInput(''); setParams({ month }) }}>Reset</ResetButton>
            </FilterActions>
          </FilterRow>
          <FilterRow sx={{ mt: 1.5 }}>
            <FilterSelect label='Courier partner' value={p.courier} allLabel='All couriers' onChange={(v) => set({ courier: v })} options={data?.couriers || []} />
            <FilterSelect label='Zone (pickup → drop)' value={p.lane} allLabel='All zones' onChange={(v) => set({ lane: v })} options={data?.lanes || []} />
            <FilterSelect label='Weight slab (kg)' value={p.slab} allLabel='All weights' onChange={(v) => set({ slab: v })} options={data?.weightSlabs || []} />
            <FilterSelect label='Approval status' value={p.status} allLabel='All statuses' onChange={(v) => set({ status: v })}
              options={['PENDING', 'APPROVED', 'REOPENED', 'BILLED'].map((s) => ({ value: s, label: STATUS_CHIP[s].label }))} />
            <FilterSelect label='Ad hoc' value={p.adhoc} allLabel='All shipments' onChange={(v) => set({ adhoc: v })}
              options={[{ value: 'WITH', label: 'With ad hoc' }, { value: 'INCOMPLETE', label: 'Ad hoc incomplete' }, { value: 'NONE', label: 'No ad hoc' }]} />
            <FilterSelect label='Margin' value={p.margin} allLabel='All margins' onChange={(v) => set({ margin: v })}
              options={[{ value: 'LOSS', label: 'Loss-making only (< 0%)' },
                ...(config?.marginFilterPcts || []).filter((x) => x !== config.lowMarginPct).map((x) => ({ value: `BELOW_${x}`, label: `Below ${x}%` })),
                { value: 'LOW', label: config ? `Below ${config.lowMarginPct}% (low margin)` : 'Below the low-margin %' }]} />
          </FilterRow>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
            {monthLabel(month)}. Zone + weight slab together give the zone × weight view. Filters and sort apply to both tabs and the cards. Amounts exclude GST.
            Lines are refreshed by "Prepare / refresh" on the Overview (and every night).
          </Typography>
        </FilterCardContent>
      </FilterCard>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{errText(error, 'Could not load the billing lines.')}</Alert>}

      <Box sx={{ ...kpiRowSx, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', mb: 2 }}>
        {statusCard('ALL', 'All shipments', 'secondary')}
        {statusCard('PENDING', 'Pending approval', 'primary')}
        {statusCard('APPROVED', 'Approved', 'success')}
        {statusCard('REOPENED', 'Re-opened', 'warning', cards.REOPENED?.count ? 'Waiting for fix' : 'None')}
        {statusCard('ADHOC_INCOMPLETE', 'Ad hoc incomplete', 'warning', cards.ADHOC_INCOMPLETE?.count ? 'Waiting for KAM / Ops' : 'All complete')}
        {statusCard('BLOCKED', 'Blocked', 'error', cards.BLOCKED?.count ? 'Fix before approving' : 'None')}
      </Box>

      {data && data.total > 0 && (
        <Box sx={{ ...kpiRowSx, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', mb: 2 }}>
          <KpiTile label='Loss shipments' accent='error' value={data.lossCount} sub={data.lossCount ? `Losing ${inr(-data.lossAmount, 0)} · worst first` : 'None in this filter'}
            onClick={() => set({ margin: 'LOSS', sort: 'marginamt_asc' })} />
          <KpiTile label='Margin (estimated)' accent='success' value={pct(marginPct)} sub={`${inr(data.margin, 0)} on ${inr(data.marginBaseTotal, 0)} with a known courier cost${leftOut} · lowest first`}
            onClick={() => set({ sort: 'margin_asc' })} />
          {data.lowestCourier && <KpiTile label='Lowest-margin courier' accent='warning' value={data.lowestCourier.key}
            sub={`${pct(data.lowestCourier.marginPct)} on ${data.lowestCourier.shipments} shipments`} onClick={() => set({ courier: data.lowestCourier.key })} />}
          {data.lowestLaneWeight && <KpiTile label='Lowest lane × weight' accent='secondary' value={`${data.lowestLaneWeight.lane} · ${data.lowestLaneWeight.weightSlab || '?'} kg`}
            sub={`${pct(data.lowestLaneWeight.marginPct)} on ${data.lowestLaneWeight.shipments} shipments`}
            onClick={() => set({ lane: data.lowestLaneWeight.lane, slab: data.lowestLaneWeight.weightSlab || '' })} />}
        </Box>
      )}

      <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_e, v) => set({ tab: v })} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value='ship' label={`Shipment view (${data?.total ?? 0})`} sx={{ textTransform: 'none' }} />
          <Tab value='client' label='Client view' sx={{ textTransform: 'none' }} />
        </Tabs>

        {tab === 'ship' && (
          <>
            <ProzoDataGrid
              columns={columns}
              rows={data?.rows || []}
              rowCount={data?.total || 0}
              paginationMode='server'
              page={page - 1}
              setPage={(pg) => set({ page: pg + 1 })}
              sizePerPage={size}
              setSizePerPage={(s) => set({ size: s })}
              rowsPerPageOptions={[20, 50, 100]}
              loading={isFetching}
              getRowId={(r) => r.id}
              checkboxSelection={billing}
              isRowSelectable={({ row }) => row.status === 'PENDING' && !row.blocked}
              rowSelectionModel={selIds}
              setRowSelectionModel={onSelection}
              keepNonExistentRowsSelected
            />
            {data && (
              <Typography sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
                Total of {data.total} shipments (all pages): base {inr(data.baseTotal)} · ad hoc {inr(data.adhocTotal)} · <b>total {inr(data.totalExclGst)}</b>. Margin {inr(data.margin)} ({pct(marginPct)}) = {inr(data.marginBaseTotal)} billed with a known courier cost − courier cost {inr(data.estCourierCost)}{leftOut}
              </Typography>
            )}
            {billing ? (
              <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, flex: 1, minWidth: 260 }}>
                  {selIds.length ? <><b>{selIds.length}</b> shipments · <b>{selClients}</b> clients selected · {inr(selAmount)}</> : 'Select pending shipments to approve.'}
                  {maxShip != null && <span style={{ color: '#637381' }}> Limit per approval: {maxShip} shipments, {maxClients} clients.</span>}
                  {overShip && <Typography component='span' sx={{ display: 'block', color: 'error.main', fontSize: 12 }}>More than {maxShip} shipments selected. Remove some or approve in batches.</Typography>}
                  {overClients && <Typography component='span' sx={{ display: 'block', color: 'error.main', fontSize: 12 }}>More than {maxClients} clients selected. Narrow the selection.</Typography>}
                </Typography>
                <LoadingButton variant='outlined' loading={selecting} disabled={maxShip == null} onClick={doSelectFirst} sx={{ textTransform: 'none' }}>Select first {maxShip ?? ''} matching</LoadingButton>
                {selIds.length > 0 && <Button onClick={() => setSelected({})} sx={{ textTransform: 'none' }}>Clear</Button>}
                <LoadingButton variant='contained' color='success' loading={approving} disabled={!selIds.length || overShip || overClients} onClick={doApprove} sx={{ textTransform: 'none' }}>
                  Approve selected
                </LoadingButton>
              </Box>
            ) : (
              <Typography sx={{ p: 2, fontSize: 12, color: 'text.secondary' }}>Approving and re-opening need PTL_BILLING access.</Typography>
            )}
          </>
        )}

        {tab === 'client' && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['Client', 'Shipments', 'Approved', 'Pending', 'Re-opened', 'Blocked', 'Ad hoc incomplete', 'Base', 'Ad hoc (client)', 'Total (excl. GST)', 'Courier cost', 'Margin', 'Margin %', 'Flag', 'Agreement', ''].map((h) => (
                    <TableCell key={h} align={['Client', 'Flag', 'Agreement', ''].includes(h) ? 'left' : 'right'} sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(clients || []).map((c) => (
                  <TableRow key={c.merchantOid}>
                    <TableCell><Button size='small' onClick={() => set({ tab: 'ship', merchant: c.merchantOid })} sx={{ textTransform: 'none', fontWeight: 600, p: 0 }}>{c.merchantName}</Button></TableCell>
                    <TableCell align='right'>{c.shipments}</TableCell><TableCell align='right'>{c.approved}</TableCell><TableCell align='right'>{c.pending}</TableCell>
                    <TableCell align='right'>{c.reopened}</TableCell><TableCell align='right' sx={{ color: c.blocked ? 'error.main' : undefined }}>{c.blocked}</TableCell>
                    <TableCell align='right'>{c.adhocIncomplete}</TableCell><TableCell align='right'>{inr(c.baseTotal, 0)}</TableCell><TableCell align='right'>{inr(c.adhocTotal, 0)}</TableCell>
                    <TableCell align='right'><b>{inr(c.totalExclGst, 0)}</b></TableCell><TableCell align='right'>{inr(c.estCourierCost, 0)}</TableCell>
                    <TableCell align='right' sx={{ color: c.margin < 0 ? 'error.main' : undefined }}>
                      {inr(c.margin, 0)}
                      {c.marginLeftOut > 0 && <Typography sx={{ fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>{c.marginLeftOut} left out</Typography>}
                    </TableCell><TableCell align='right'>{pct(c.marginPct)}</TableCell>
                    <TableCell>{c.marginFlag && <Chip size='small' color={c.marginFlag === 'LOSS' ? 'error' : c.marginFlag === 'LOW' ? 'warning' : 'success'} label={c.marginFlag === 'LOW' ? 'LOW MARGIN' : c.marginFlag} />}</TableCell>
                    <TableCell><AgreementChips signed={c.agreementSigned} validity={c.agreementValidity} />{c.agreementExpiry && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>till {fmtDate(c.agreementExpiry)}{c.agreementCreditDays != null ? ` · ${creditDaysText(c.agreementCreditDays)}` : ''}</Typography>}</TableCell>
                    <TableCell><Button size='small' variant='outlined' onClick={() => set({ tab: 'ship', merchant: c.merchantOid })} sx={{ textTransform: 'none', borderRadius: 5 }}>Review shipments</Button></TableCell>
                  </TableRow>
                ))}
                {!clientsLoading && (clients || []).length === 0 && <TableRow><TableCell colSpan={16} sx={{ color: 'text.secondary' }}>No billing lines for these filters. Run "Prepare / refresh" on the Overview.</TableCell></TableRow>}
              </TableBody>
            </Table>
            <Typography sx={{ px: 2, py: 1, fontSize: 12, color: 'text.secondary' }}>
              Courier cost, margin and margin % count only shipments with a stored price and a courier cost; "left out" shipments are in the total but not in the margin.
            </Typography>
          </Box>
        )}
      </Card>

      {detailId && <LineDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
      {reopenRow && <ReopenDialog row={reopenRow} onClose={() => setReopenRow(null)} />}
    </Box>
  )
}

function ReopenDialog({ row, onClose }) {
  const [note, setNote] = useState('')
  const [reopen, { isLoading }] = useReopenLineMutation()
  const { enqueueSnackbar } = useSnackbar()
  const submit = async () => {
    const res = await reopen({ id: row.id, version: row.version, note })
    if (res.error) return enqueueSnackbar(errText(res.error), { variant: 'error' })
    enqueueSnackbar(`${row.awb} re-opened`, { variant: 'success' })
    onClose()
  }
  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Re-open {row.awb}<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.merchantName} · it goes back for fixing and re-approval</Typography></DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth multiline minRows={3} label='What needs fixing? (required)' value={note} onChange={(e) => setNote(e.target.value)}
          placeholder='e.g. Weight looks wrong, recheck with the courier' inputProps={{ maxLength: 500 }} sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant='contained' color='warning' loading={isLoading} disabled={note.trim().length < 5} onClick={submit}>Re-open</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
